import { FieldEmail, FieldSelect, FieldText } from '@explorer/forms';
import { ControlEscalationPriority } from '@explorer/open-api-client';
import { IntlShape } from 'react-intl';
import * as yup from 'yup';

export interface ContactFormValues {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  escalation_priority: ControlEscalationPriority;
}

export const validationSchema = (intl: IntlShape) =>
  yup.object({
    email: yup
      .string()
      .email(
        intl.formatMessage({
          id: 'mdr.escalation-contacts.modal.email-invalid',
        }),
      )
      .required(intl.formatMessage({ id: 'forms-errors.required-email' }))
      .trim(),
    first_name: yup
      .string()
      .when('escalation_priority', {
        is: (priority: ControlEscalationPriority) =>
          priority === ControlEscalationPriority.TierOneAllAlerts ||
          priority === ControlEscalationPriority.TierTwoBackupAlerts,
        then: (schema) =>
          schema.required(
            intl.formatMessage({
              id: 'mdr.escalation-contacts.validation.first-name-required',
            }),
          ),
        otherwise: (schema) => schema.notRequired(),
      })
      .trim(),
    last_name: yup
      .string()
      .when('escalation_priority', {
        is: (priority: ControlEscalationPriority) =>
          priority === ControlEscalationPriority.TierOneAllAlerts ||
          priority === ControlEscalationPriority.TierTwoBackupAlerts,
        then: (schema) =>
          schema.required(
            intl.formatMessage({
              id: 'mdr.escalation-contacts.validation.last-name-required',
            }),
          ),
        otherwise: (schema) => schema.notRequired(),
      })
      .trim(),
    phone: yup
      .string()
      .when('escalation_priority', {
        is: (priority: ControlEscalationPriority) =>
          priority === ControlEscalationPriority.TierOneAllAlerts ||
          priority === ControlEscalationPriority.TierTwoBackupAlerts,
        then: (schema) =>
          schema.required(
            intl.formatMessage({
              id: 'mdr.escalation-contacts.validation.phone-required',
            }),
          ),
        otherwise: (schema) => schema.notRequired(),
      })
      .trim(),
    escalation_priority: yup
      .string()
      .oneOf(Object.values(ControlEscalationPriority))
      .required(
        intl.formatMessage({
          id: 'mdr.escalation-contacts.validation.priority-required',
        }),
      ),
  });

export const initializeFormSchema = ({ formId, formik }) => ({
  email: {
    formId,
    id: `${formId}-email`,
    name: 'email',
    formik,
    renderField: (props) => (
      <FieldEmail
        {...props}
        label={{ id: 'mdr.escalation-contacts.modal.email' }}
        required
      />
    ),
  },
  escalation_priority: {
    formId,
    id: `${formId}-escalation_priority`,
    name: 'escalation_priority',
    formik,
    renderField: (props) => (
      <FieldSelect
        {...props}
        required
        label={{ id: 'mdr.escalation-contacts.modal.priority' }}
        options={[
          {
            label: {
              id: 'mdr.escalation-contacts.priority.primary',
            },
            value: ControlEscalationPriority.TierOneAllAlerts,
          },
          {
            label: {
              id: 'mdr.escalation-contacts.priority.backup',
            },
            value: ControlEscalationPriority.TierTwoBackupAlerts,
          },
          {
            label: {
              id: 'mdr.escalation-contacts.priority.none',
            },
            value: ControlEscalationPriority.None,
          },
        ]}
      />
    ),
  },
  first_name: {
    formId,
    id: `${formId}-first_name`,
    name: 'first_name',
    formik,
    renderField: (props) => {
      const isRequired =
        formik.values.escalation_priority ===
          ControlEscalationPriority.TierOneAllAlerts ||
        formik.values.escalation_priority ===
          ControlEscalationPriority.TierTwoBackupAlerts;
      return (
        <FieldText
          required={isRequired}
          {...props}
          label={{ id: 'mdr.escalation-contacts.modal.first-name' }}
        />
      );
    },
  },
  last_name: {
    formId,
    id: `${formId}-last_name`,
    name: 'last_name',
    formik,
    renderField: (props) => {
      const isRequired =
        formik.values.escalation_priority ===
          ControlEscalationPriority.TierOneAllAlerts ||
        formik.values.escalation_priority ===
          ControlEscalationPriority.TierTwoBackupAlerts;
      return (
        <FieldText
          required={isRequired}
          {...props}
          label={{ id: 'mdr.escalation-contacts.modal.last-name' }}
        />
      );
    },
  },
  phone: {
    formId,
    id: `${formId}-phone`,
    name: 'phone',
    formik,
    renderField: (props) => {
      const isRequired =
        formik.values.escalation_priority ===
          ControlEscalationPriority.TierOneAllAlerts ||
        formik.values.escalation_priority ===
          ControlEscalationPriority.TierTwoBackupAlerts;
      return (
        <FieldText
          required={isRequired}
          {...props}
          label={{ id: 'mdr.escalation-contacts.modal.phone' }}
          type="tel"
        />
      );
    },
  },
});
